import classNames from "classnames";
import { Form, Formik } from "formik";
import * as React from "react";
import { useTranslation } from "react-i18next";

import style from "./delivery-history.scss";
import { Result } from "./DeliveryHistoryMenuItems";
import { createProductsMap } from "components/licenses/delivery-history/ViewDeliveryHistory";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import Modal from "components/modal/Modal";
import StaticTable from "components/support/api-guide/StaticTable";
import DateCell from "components/table/DateCell";
import { licenseService } from "services/licenses/LicenseService";
import buttons from "styles/buttons.scss";
import form from "styles/form.scss";

import testIds from "testIds.json";

const ALL_PRODUCTS = createProductsMap();

interface DeliveryLicenses {
    licenseId: string;
    amount: number;
    expirationDate: string;
}

interface CloneDeliveryData {
    deliveryType: string;
    caseNumber: string;
    opportunityId: string;
    notes: string;
}

interface CloneDeliveryModalProps {
    isOpen: boolean;
    onClose: () => void;
    deliveryHistoryId: string;
    initialLoading: boolean;
    cloneDeliveryData?: CloneDeliveryData;
    licenses?: DeliveryLicenses[];
    abortControllers: AbortController[];
    setInitialLoading: (value: boolean) => void;
    showResult: (value: Result) => void;
}

const CloneDeliveryModal: React.FC<CloneDeliveryModalProps> = ({
    isOpen,
    onClose,
    deliveryHistoryId,
    initialLoading,
    cloneDeliveryData,
    licenses,
    abortControllers,
    setInitialLoading,
    showResult,
}) => {
    const { t } = useTranslation();

    const licenseStaticTable = (licenses: DeliveryLicenses[] | undefined) => {
        return (
            licenses !== undefined && (
                <StaticTable
                    headers={[
                        {
                            value: t("DeliveryHistory.editLicenseDelivery.table.licenseType"),
                        },
                        {
                            value: t("DeliveryHistory.editLicenseDelivery.table.amount"),
                        },
                        {
                            value: t("DeliveryHistory.editLicenseDelivery.table.expirationDate"),
                        },
                    ]}
                    cells={licenses.map((license, key) => {
                        return [
                            <div
                                data-testid={
                                    testIds.workArea.license.deliveryHistory.editDeliveryDialog.table.licenseTypeLabel
                                }
                                key={license.licenseId}
                            >
                                {ALL_PRODUCTS.get(license.licenseId)}
                            </div>,
                            <div
                                data-testid={
                                    testIds.workArea.license.deliveryHistory.editDeliveryDialog.table
                                        .totalOfLicensesLabel
                                }
                                key={"amount" + key}
                            >
                                {license.amount}
                            </div>,
                            <div
                                data-testid={
                                    testIds.workArea.license.deliveryHistory.editDeliveryDialog.table
                                        .expirationDateLabel
                                }
                                key={"date" + key}
                            >
                                <DateCell tooltip={false} value={license.expirationDate} />
                            </div>,
                        ];
                    })}
                    testId={testIds.workArea.license.deliveryHistory.editDeliveryDialog.table.itself}
                />
            )
        );
    };

    return (
        <Modal
            isOpen={isOpen}
            hideModal={onClose}
            modalTitle={t("DeliveryHistory.deliveryHistoryTable.kebabMenu.cloneDelivery")}
        >
            <div>
                {!initialLoading ? (
                    <Formik
                        initialValues={{
                            deliveryInterval: "",
                            clones: "",
                        }}
                        onSubmit={async (values) => {
                            const abortController = new AbortController();
                            abortControllers.push(abortController);
                            setInitialLoading(true);
                            try {
                                await licenseService.cloneLicenseDelivery(
                                    deliveryHistoryId,
                                    values.deliveryInterval,
                                    values.clones,
                                    abortController
                                );
                                onClose();
                                showResult({
                                    title: t("DeliveryHistory.cloneDelivery.successTitle"),
                                    message: t("DeliveryHistory.cloneDelivery.successMessage"),
                                });
                            } catch {
                                onClose();
                                setInitialLoading(false);
                                showResult({
                                    title: t("DeliveryHistory.cloneLicenseDeilvery.failureTitle"),
                                    message: t("DeliveryHistory.cloneLicenseDeilvery.failureMessage"),
                                });
                            }
                        }}
                    >
                        {({ values, handleChange, errors }) => (
                            <Form>
                                <div className={form.formFields}>
                                    <label
                                        htmlFor="deliveryInterval"
                                        className={classNames(form.label, style.wideWidth)}
                                    >
                                        {t("DeliveryHistory.cloneLicenseDeilvery.interval")}
                                    </label>
                                    <div className={form.formFieldsColumnDirection}>
                                        <input
                                            id="deliveryInterval"
                                            className={classNames(form.input, form.fixedWidthInput)}
                                            onChange={(e) => {
                                                const numericValue = e.target.value.replace(/[^0-9]/g, "");
                                                handleChange({
                                                    target: { name: "deliveryInterval", value: numericValue },
                                                });
                                            }}
                                            value={values.deliveryInterval}
                                            min="1"
                                            max="1000"
                                            type="number"
                                        />
                                        {errors.deliveryInterval && (
                                            <div className={form.error}>{errors.deliveryInterval}</div>
                                        )}
                                    </div>
                                </div>

                                <div className={form.formFields}>
                                    <label htmlFor="clones" className={classNames(form.label, style.wideWidth)}>
                                        {t("DeliveryHistory.cloneLicenseDeilvery.clones")}
                                    </label>
                                    <div className={form.formFieldsColumnDirection}>
                                        <input
                                            id="clones"
                                            className={classNames(form.input, form.fixedWidthInput)}
                                            onChange={(e) => {
                                                const numericValue = e.target.value.replace(/[^0-9]/g, "");
                                                handleChange({ target: { name: "clones", value: numericValue } });
                                            }}
                                            value={values.clones}
                                            min="1"
                                            max="20"
                                            type="number"
                                        />
                                        {errors.clones && <div className={form.error}>{errors.clones}</div>}
                                    </div>
                                </div>
                                <div className={form.formFields}>
                                    <label htmlFor="clones" className={classNames(form.label, style.wideWidth)}>
                                        {t("DeliveryHistory.deliveryHistoryTable.deliveryType")}
                                    </label>
                                    <div className={classNames(form.label, form.fixedWidthInput)}>
                                        {cloneDeliveryData?.deliveryType}
                                    </div>
                                </div>
                                <div className={form.formFields}>
                                    <label htmlFor="clones" className={classNames(form.label, style.wideWidth)}>
                                        {t("DeliveryHistory.deliveryHistoryDialog.opportunityId")}
                                    </label>
                                    <div className={classNames(form.label, form.fixedWidthInput)}>
                                        {cloneDeliveryData?.opportunityId}
                                    </div>
                                </div>
                                <div className={form.formFields}>
                                    <label htmlFor="clones" className={classNames(form.label, style.wideWidth)}>
                                        {t("DeliveryHistory.deliveryHistoryDialog.caseNumber")}
                                    </label>
                                    <div className={classNames(form.label, form.fixedWidthInput)}>
                                        {cloneDeliveryData?.caseNumber}
                                    </div>
                                </div>
                                {licenseStaticTable(licenses)}
                                <div className={form.buttonContainer}>
                                    <button
                                        type="submit"
                                        disabled={
                                            !values.deliveryInterval || !values.clones || Object.keys(errors).length > 0
                                        }
                                        className={classNames(buttons.primaryButtonWithoutIcon, form.submitButton)}
                                    >
                                        {t("Common.clone")}
                                    </button>
                                    <button
                                        type={"button"}
                                        className={buttons.secondaryButtonWithoutIcon}
                                        onClick={onClose}
                                        data-testid={testIds.common.dialog.closeButton}
                                    >
                                        {t("Common.cancel")}
                                    </button>
                                </div>
                            </Form>
                        )}
                    </Formik>
                ) : (
                    <div>
                        <LoadingIndicator />
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default CloneDeliveryModal;
